import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink, FileText, Star, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api, errorText, openFile, unwrap, uploadForm } from '../../../api/client';
import type { AssetDetail, Attachment } from '../../../api/types';
import { useAuth } from '../../../auth/AuthProvider';
import { AuthImage } from '../../../components/AuthImage';
import { Badge, Button, Card, CardHeader, Checkbox, ConfirmDialog, Dialog, EmptyState, ErrorBox, Field, Input, Loading, Select, Table, Td, Th, Tr, useToast } from '../../../components/ui';
import { DOCUMENT_CATEGORY_LABELS, formatBytes, formatDateTime, PHOTO_CATEGORY_LABELS } from '../../../lib/format';

const PROTECTED = new Set(['disposal_protocol', 'disposal_certificate']);

export function FilesTab({ asset }: { asset: AssetDetail }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [upload, setUpload] = useState<'document' | 'photo' | null>(null);
  const [deleting, setDeleting] = useState<Attachment | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const path = { ref: asset.id };
  const include_deleted = showDeleted ? ('true' as const) : undefined;
  const docs = useQuery({
    queryKey: ['asset', asset.id, 'documents', showDeleted],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}/documents', { params: { path, query: { include_deleted } } })),
  });
  const photos = useQuery({
    queryKey: ['asset', asset.id, 'photos', showDeleted],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}/photos', { params: { path, query: { include_deleted } } })),
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['asset'] });

  const setPrimary = useMutation({
    mutationFn: (id: string) => unwrap(api.PATCH('/api/attachments/{id}', { params: { path: { id } }, body: { is_primary: true } })),
    onSuccess: () => {
      refresh();
      toast('Hauptfoto gesetzt');
    },
    onError: (e) => toast(errorText(e), 'error'),
  });

  const del = useMutation({
    mutationFn: (reason: string) => unwrap(api.DELETE('/api/attachments/{id}', { params: { path: { id: deleting!.id } }, body: { reason } })),
    onSuccess: () => {
      refresh();
      toast('Datei gelöscht');
      setDeleting(null);
    },
  });

  const canUpload = can('attachment:upload') && !asset.deleted_at;
  const canDelete = can('attachment:delete');

  const open = (a: Attachment, download = false) =>
    openFile(a.content_url + (download ? '?disposition=attachment' : ''), download ? { download: a.original_filename } : {}).catch((e: unknown) =>
      toast(e instanceof Error ? e.message : 'Datei konnte nicht geöffnet werden', 'error'),
    );

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Checkbox label="Gelöschte Dateien anzeigen" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
      </div>
      <Card>
        <CardHeader
          title="Dokumente"
          description="Rechnungen, Garantie, Wartung, Verschrottungsprotokolle"
          actions={
            canUpload && (
              <Button size="sm" icon={<Upload className="size-4" />} onClick={() => setUpload('document')}>
                Dokument hochladen
              </Button>
            )
          }
        />
        {docs.isPending ? (
          <Loading />
        ) : docs.error ? (
          <ErrorBox error={docs.error} className="m-4" />
        ) : docs.data.data.length === 0 ? (
          <EmptyState title="Keine Dokumente" icon={<FileText className="size-10" />} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Datei</Th>
                <Th>Kategorie</Th>
                <Th>Größe</Th>
                <Th>Hochgeladen</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {docs.data.data.map((d) => (
                <Tr key={d.id} className={d.deleted_at ? 'opacity-50' : undefined}>
                  <Td>
                    <button type="button" className="text-left font-medium text-brand-700 hover:underline" onClick={() => open(d)}>
                      {d.title || d.original_filename}
                    </button>
                    {d.title && <div className="text-xs text-slate-500">{d.original_filename}</div>}
                    {d.deleted_at && <div className="text-xs text-red-600">gelöscht: {d.delete_reason}</div>}
                  </Td>
                  <Td>
                    <Badge tone={PROTECTED.has(d.category) ? 'amber' : 'gray'}>{DOCUMENT_CATEGORY_LABELS[d.category] ?? d.category}</Badge>
                  </Td>
                  <Td className="whitespace-nowrap">{formatBytes(d.size_bytes)}</Td>
                  <Td className="text-xs whitespace-nowrap text-slate-500">{formatDateTime(d.uploaded_at)}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" aria-label="Öffnen" icon={<ExternalLink className="size-4" />} onClick={() => open(d)} />
                    <Button size="sm" variant="ghost" aria-label="Herunterladen" icon={<Download className="size-4" />} onClick={() => open(d, true)} />
                    {canDelete && !d.deleted_at && !PROTECTED.has(d.category) && (
                      <Button size="sm" variant="ghost" aria-label="Löschen" icon={<Trash2 className="size-4" />} onClick={() => setDeleting(d)} />
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Fotos"
          description="Gesamtansicht, Typenschild, Seriennummer, Zustand"
          actions={
            canUpload && (
              <Button size="sm" icon={<Upload className="size-4" />} onClick={() => setUpload('photo')}>
                Foto hochladen
              </Button>
            )
          }
        />
        {photos.isPending ? (
          <Loading />
        ) : photos.error ? (
          <ErrorBox error={photos.error} className="m-4" />
        ) : photos.data.data.length === 0 ? (
          <EmptyState title="Keine Fotos" />
        ) : (
          <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 lg:grid-cols-4">
            {photos.data.data.map((p) => (
              <figure key={p.id} className={`overflow-hidden rounded-md border border-slate-200 ${p.deleted_at ? 'opacity-50' : ''}`}>
                <button type="button" className="block w-full" onClick={() => open(p)} aria-label={`Foto ${p.original_filename} öffnen`}>
                  <AuthImage src={p.content_url} alt={p.title ?? p.original_filename} className="aspect-square w-full" />
                </button>
                <figcaption className="space-y-1 p-2 text-xs">
                  <div className="flex items-center gap-1">
                    <Badge>{PHOTO_CATEGORY_LABELS[p.category] ?? p.category}</Badge>
                    {p.is_primary && (
                      <Badge tone="amber">
                        <Star className="size-3" /> Haupt
                      </Badge>
                    )}
                  </div>
                  <div className="truncate text-slate-600" title={p.original_filename}>
                    {p.title || p.original_filename}
                  </div>
                  {!p.deleted_at && (
                    <div className="flex gap-1">
                      {canUpload && !p.is_primary && (
                        <Button size="sm" variant="ghost" icon={<Star className="size-3.5" />} onClick={() => setPrimary.mutate(p.id)}>
                          Hauptfoto
                        </Button>
                      )}
                      {canDelete && <Button size="sm" variant="ghost" aria-label="Löschen" icon={<Trash2 className="size-3.5" />} onClick={() => setDeleting(p)} />}
                    </div>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </Card>

      <UploadDialog asset={asset} kind={upload} onClose={() => setUpload(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Datei löschen"
        message={`„${deleting?.original_filename}“ wird ausgeblendet (bleibt revisionssicher gespeichert).`}
        danger
        requireReason
        confirmLabel="Löschen"
        loading={del.isPending}
        error={del.error}
        onConfirm={(r) => del.mutate(r)}
      />
    </div>
  );
}

function UploadDialog({ asset, kind, onClose }: { asset: AssetDetail; kind: 'document' | 'photo' | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [primary, setPrimary] = useState(false);
  const labels = kind === 'photo' ? PHOTO_CATEGORY_LABELS : DOCUMENT_CATEGORY_LABELS;

  useEffect(() => {
    if (kind) {
      setFile(null);
      setCategory(kind === 'photo' ? 'overview' : 'invoice');
      setTitle('');
      setPrimary(kind === 'photo' && asset.photo_count === 0);
    }
  }, [kind, asset.photo_count]);

  const m = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file!);
      form.append('category', category);
      if (title.trim()) form.append('title', title.trim());
      if (kind === 'photo') form.append('is_primary', primary ? 'true' : 'false');
      return uploadForm(`/api/assets/${encodeURIComponent(asset.id)}/${kind === 'photo' ? 'photos' : 'documents'}`, form);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['asset'] });
      toast('Hochgeladen');
      onClose();
    },
  });

  return (
    <Dialog
      open={!!kind}
      onClose={onClose}
      title={kind === 'photo' ? 'Foto hochladen' : 'Dokument hochladen'}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!file}>
            Hochladen
          </Button>
        </>
      }
    >
      <Field label="Datei" required hint={kind === 'photo' ? 'JPEG, PNG, WebP, HEIC' : 'PDF, Bilder, Office-Dokumente'}>
        {(p) => (
          <input
            {...p}
            ref={fileRef}
            type="file"
            accept={kind === 'photo' ? 'image/*' : undefined}
            capture={kind === 'photo' ? 'environment' : undefined}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100"
          />
        )}
      </Field>
      <Field label="Kategorie">
        {(p) => (
          <Select {...p} value={category} onChange={(e) => setCategory(e.target.value)}>
            {Object.entries(labels)
              .filter(([k]) => k !== 'disposal_protocol')
              .map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
          </Select>
        )}
      </Field>
      <Field label="Titel (optional)">{(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
      {kind === 'photo' && <Checkbox label="Als Hauptfoto verwenden" checked={primary} onChange={(e) => setPrimary(e.target.checked)} />}
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
